
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { DollarSign, BarChart, Clock, TrendingUp, Building, Shield } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { getAuthUrl } from "@/utils/redirectUtils";

const BusinessOwnersPage = () => {
  const benefits = [
    { 
      icon: <DollarSign className="h-10 w-10 text-primary" />, 
      title: "Clear ROI Dashboard", 
      description: "See exactly how much you're making from every dollar spent. No confusing metrics - just real revenue numbers."
    },
    { 
      icon: <Clock className="h-10 w-10 text-primary" />, 
      title: "Automated Marketing", 
      description: "Set it up once and let AI handle your campaigns while you focus on running your business."
    },
    { 
      icon: <Building className="h-10 w-10 text-primary" />, 
      title: "No Agency Needed", 
      description: "Skip the expensive middleman. Manage your own ads with simple tools designed for business owners."
    },
    { 
      icon: <BarChart className="h-10 w-10 text-primary" />, 
      title: "Simple Reports", 
      description: "Get weekly reports that actually make sense. See what's working and what needs fixing in plain English."
    },
    { 
      icon: <TrendingUp className="h-10 w-10 text-primary" />, 
      title: "Grow Your Revenue", 
      description: "AI finds the right customers for your business and brings them to your door. More leads, more sales."
    },
    { 
      icon: <Shield className="h-10 w-10 text-primary" />, 
      title: "Protect Your Brand", 
      description: "Make sure your business shows up when customers search for you. Stay ahead of competitors."
    },
  ];

  const howItWorks = [
    {
      step: "1",
      title: "Tell Us About Your Business",
      description: "Just answer a few simple questions about what you sell and who your customers are. Takes 5 minutes."
    },
    {
      step: "2", 
      title: "AI Creates Your Campaigns",
      description: "Our AI builds professional Google Ads campaigns automatically. No complicated setup or confusing options."
    },
    {
      step: "3",
      title: "Watch Your ROI Dashboard",
      description: "See real results in dollars and cents. Track exactly how much revenue your ads are generating."
    }
  ];

  const costComparison = [
    { service: "Traditional Agency", cost: "$3,000-8,000/month", setup: "3-6 months", control: "Limited" },
    { service: "Upfixe Platform", cost: "$99–599/month", setup: "Same day", control: "Full control" }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Google Ads for CEOs & Business Owners | Upfixe</title>
        <meta name="description" content="Control your own Google Ads without expensive agencies. AI-powered campaigns, transparent reporting, predictable costs. Skip the middleman." />
        <meta name="keywords" content="Google Ads for business owners, small business Google Ads, in-house Google Ads, CEO marketing" />
        <meta property="og:title" content="Google Ads for CEOs & Business Owners | Upfixe" />
        <meta property="og:description" content="Control your own Google Ads without expensive agencies. AI-powered campaigns, transparent reporting, predictable costs." />
        <link rel="canonical" href="https://upfixe.com/business-owners" />
      </Helmet>
      <Header />
      
      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6">
          {/* Hero Section */}
          <div className="flex flex-col md:flex-row gap-8 mb-16 items-center">
            <div className="md:w-1/2">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Google Ads Simplified for Business Owners</h1>
              <h2 className="text-2xl text-primary mb-6">Get clear ROI without the agency fees</h2>
              <p className="text-lg text-muted-foreground mb-8">
                Stop paying thousands to marketing agencies who speak in confusing jargon. Our AI makes Google Ads simple - you see exactly what you're making from every dollar spent.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="text-lg px-8 h-14 font-bold"
                  onClick={() => window.location.href = getAuthUrl()}
                >
                  Get My Free Account
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="text-lg px-8 h-14 font-bold"
                  onClick={() => window.location.href = "#how-it-works"}
                >
                  See How It Works
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                No credit card required. Setup takes 5 minutes.
              </p>
            </div>
            <div className="md:w-1/2">
              <div className="aspect-video rounded-2xl overflow-hidden border-2 border-primary/20 shadow-lg">
                <iframe
                  src="https://www.youtube.com/embed/BjsoKpVm9vc"
                  title="Upfixe Business Owner Campaign Demo"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>

          {/* Problem Section */}
          <div className="mb-16 bg-red-50 dark:bg-red-950/20 p-8 rounded-lg">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-4 text-red-900 dark:text-red-100">Tired of Expensive Marketing Agencies?</h2>
              <p className="text-lg text-red-700 dark:text-red-200 max-w-3xl mx-auto">
                You're not alone. Most business owners waste thousands on agencies that can't show clear results.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="text-4xl mb-2">😵</div>
                <h3 className="font-bold mb-2">Confusing Reports</h3>
                <p className="text-sm text-muted-foreground">
                  Agencies send complex reports with metrics that don't show real revenue
                </p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-2">💸</div>
                <h3 className="font-bold mb-2">High Monthly Fees</h3>
                <p className="text-sm text-muted-foreground">
                  $3,000-8,000 per month just for someone else to manage your ads
                </p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-2">🤷</div>
                <h3 className="font-bold mb-2">No Real Control</h3>
                <p className="text-sm text-muted-foreground">
                  You can't make quick changes or know what's really happening
                </p>
              </div>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="mb-16">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold mb-4">Finally, Google Ads That Make Sense</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Built specifically for business owners who want clear results without the complexity.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <Card key={index} className="bg-card border-primary/10">
                  <CardHeader className="pb-2">
                    <div className="mb-4">{benefit.icon}</div>
                    <CardTitle className="text-xl">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* How It Works Section */}
          <div id="how-it-works" className="mb-16 bg-primary/5 p-8 rounded-lg">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold mb-4">How It Works (Really Simple)</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                No technical setup. No complicated training. Just three simple steps to profitable ads.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col gap-8">
                {howItWorks.map((step, index) => (
                  <div key={index}>
                    <div className="flex flex-col md:flex-row gap-6 items-start">
                      <div className="md:w-1/3 flex flex-col items-center">
                        <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center text-white text-2xl font-bold mb-4">{step.step}</div>
                        <h3 className="text-xl font-bold text-center">{step.title}</h3>
                      </div>
                      <div className="md:w-2/3">
                        <p className="text-lg">{step.description}</p>
                      </div>
                    </div>
                    {index < howItWorks.length - 1 && <Separator className="my-6" />}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Cost Comparison Section */}
          <div className="mb-16">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold mb-4">Stop Paying Agency Fees</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                See how much money you'll save by managing your own ads with our simple platform.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border-2 border-red-200">
                  <CardHeader>
                    <CardTitle className="text-red-600">Traditional Marketing Agency</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <span className="font-bold">Monthly Cost: </span>
                      <span className="text-red-600 font-bold">$3,000-8,000</span>
                    </div>
                    <div>
                      <span className="font-bold">Setup Time: </span>
                      <span>3-6 months</span>
                    </div>
                    <div>
                      <span className="font-bold">Control: </span>
                      <span>Limited - they manage everything</span>
                    </div>
                    <div>
                      <span className="font-bold">Reports: </span>
                      <span>Confusing metrics, unclear ROI</span>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-2 border-primary bg-primary/5">
                  <CardHeader>
                    <CardTitle className="text-primary">Upfixe Platform</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <span className="font-bold">Monthly Cost: </span>
                      <span className="text-primary font-bold">From $99</span>
                    </div>
                    <div>
                      <span className="font-bold">Setup Time: </span>
                      <span>Same day</span>
                    </div>
                    <div>
                      <span className="font-bold">Control: </span>
                      <span>Full control - you own everything</span>
                    </div>
                    <div>
                      <span className="font-bold">Reports: </span>
                      <span>Clear revenue numbers in plain English</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <div className="text-center mt-8 p-6 bg-green-50 dark:bg-green-950/20 rounded-lg">
                <h3 className="text-2xl font-bold text-green-800 dark:text-green-200 mb-2">
                  Save $36,000+ Per Year
                </h3>
                <p className="text-green-700 dark:text-green-300">
                  That's enough to hire a full-time employee or invest back into your business growth.
                </p>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="mb-16">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold mb-4">Real Results from Real Business Owners</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                See the actual revenue improvements our customers achieve.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <Card className="text-center">
                <CardContent className="pt-6">
                  <div className="text-3xl font-bold text-primary mb-2">312%</div>
                  <p className="text-sm">Average ROI improvement in first 90 days</p>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="pt-6">
                  <div className="text-3xl font-bold text-primary mb-2">$47K</div>
                  <p className="text-sm">Average annual savings vs agency fees</p>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="pt-6">
                  <div className="text-3xl font-bold text-primary mb-2">15 hrs</div>
                  <p className="text-sm">Time saved per month vs managing ads manually</p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Blog Links Section */}
          <div className="py-20 px-4 bg-blue-50 rounded-2xl mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Master Google Ads on Your Own Terms</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Learn how to optimize your Google Ads without expensive agencies or hiring specialists.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <a href="/blog/reduce-google-ads-cpa" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">Reduce Google Ads CPA</h3>
                <p className="text-sm text-muted-foreground mb-4">7 proven strategies to cut your cost-per-acquisition in half.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article →</span>
              </a>
              <a href="/blog/budget-optimization-guide" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">Budget Optimization Guide</h3>
                <p className="text-sm text-muted-foreground mb-4">Complete framework for maximizing ROI on every dollar spent.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article →</span>
              </a>
              <a href="/blog/ai-vs-manual-google-ads" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">AI vs Manual Ads</h3>
                <p className="text-sm text-muted-foreground mb-4">Compare costs and results: agencies vs AI platforms vs DIY.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article →</span>
              </a>
            </div>
          </div>

          {/* Final CTA */}
          <Card className="max-w-3xl mx-auto mb-16 bg-primary/5 border-primary/20">
            <CardHeader className="text-center">
              <CardTitle className="text-3xl">Ready to Fire Your Marketing Agency?</CardTitle>
              <CardDescription className="text-lg">
                Join 2,847 business owners who took control of their Google Ads and boosted their profits.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <div className="space-y-4">
                <Button 
                  size="lg" 
                  className="text-lg px-12 h-16 font-bold"
                  onClick={() => window.location.href = getAuthUrl()}
                >
                  Get My Free Account
                </Button>
                <p className="text-sm text-muted-foreground">
                  No credit card required • Setup takes 5 minutes • Cancel anytime
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default BusinessOwnersPage;
